import { z } from 'zod';

/**
 * @aiPurpose closed catalog of Lead source intake outcomes (#1076).
 *   Mirrors the Prisma `LeadSourceDeliveryOutcome` enum.
 * @aiInvariant append-only; delivery rows reference these literals
 *   forever.
 */
export enum LeadSourceDeliveryOutcome {
  ACCEPTED = 'ACCEPTED',
  REJECTED_SIGNATURE = 'REJECTED_SIGNATURE',
  REJECTED_REPLAY = 'REJECTED_REPLAY',
  REJECTED_TIMESTAMP = 'REJECTED_TIMESTAMP',
  REJECTED_IDEMPOTENCY_MATCH = 'REJECTED_IDEMPOTENCY_MATCH',
  REJECTED_VALIDATION = 'REJECTED_VALIDATION',
  REJECTED_DISABLED = 'REJECTED_DISABLED',
  REJECTED_UNKNOWN = 'REJECTED_UNKNOWN',
  // WHY: server-side failures during lead creation/log persistence must
  // be distinguishable from partner-side validation in the audit log so
  // operators can triage outages without trawling for which 500s were
  // ours vs. theirs (#1078 acceptance criterion).
  INTERNAL_ERROR = 'INTERNAL_ERROR',
}
export const LeadSourceDeliveryOutcomeSchema = z.nativeEnum(LeadSourceDeliveryOutcome);

export const LEAD_SOURCE_CODE_MAX = 64;
export const LEAD_SOURCE_DISPLAY_NAME_MAX = 128;
export const LEAD_SOURCE_SECRET_MIN = 32;
export const LEAD_SOURCE_SECRET_MAX = 128;
export const LEAD_SOURCE_CODE_REGEX = /^[a-z0-9_]+$/;

/**
 * @aiPurpose public wire projection of a `LeadSource` row. The HMAC
 *   `secret` is INTENTIONALLY OMITTED from this shape so it cannot
 *   leak through ordinary list/read endpoints.
 * @aiSecurity any field added here must not contain secret material;
 *   use `LeadSourceSecretRevealSchema` for the one-time create/rotate
 *   reveal envelope.
 */
export const LeadSourceSchema = z
  .object({
    id: z.string().uuid(),
    code: z
      .string()
      .min(1)
      .max(LEAD_SOURCE_CODE_MAX)
      .regex(LEAD_SOURCE_CODE_REGEX, 'must be lowercase letters/digits/underscore'),
    displayName: z.string().min(1).max(LEAD_SOURCE_DISPLAY_NAME_MAX),
    enabled: z.boolean(),
    secretIssuedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type LeadSource = z.infer<typeof LeadSourceSchema>;

/**
 * @aiPurpose one-time reveal envelope returned by source create/rotate.
 *   The plaintext `secret` field is shown exactly once; subsequent
 *   reads never include it.
 * @aiSecurity callers must surface the secret to the operator and
 *   then discard it from memory/logs. Audit must not record the
 *   secret value; only `secretIssuedAt`.
 */
export const LeadSourceSecretRevealSchema = z
  .object({
    source: LeadSourceSchema,
    secret: z.string().min(LEAD_SOURCE_SECRET_MIN).max(LEAD_SOURCE_SECRET_MAX),
  })
  .strict();
export type LeadSourceSecretReveal = z.infer<typeof LeadSourceSecretRevealSchema>;

export const LEAD_SOURCE_IDEMPOTENCY_KEY_MAX = 128;
export const LEAD_SOURCE_NONCE_MAX = 128;
export const LEAD_SOURCE_PAYLOAD_HASH_MAX = 128;

/**
 * @aiPurpose wire projection of a `LeadSourceDeliveryLog` row.
 *   `rawPayload` is OMITTED from the ordinary projection — it is
 *   exposed only through the `sources.manage`-gated debug endpoint
 *   and only until `payloadPurgedAt` is set (#1075 retention).
 * @aiPii `rawPayload` may carry partner-side PII. `metadata` must
 *   contain typed delivery diagnostics only.
 */
export const LeadSourceDeliveryLogSchema = z
  .object({
    id: z.string().uuid(),
    sourceId: z.string().uuid().nullable(),
    outcome: LeadSourceDeliveryOutcomeSchema,
    httpStatus: z.number().int().min(100).max(599),
    leadId: z.string().uuid().nullable(),
    idempotencyKey: z.string().min(1).max(LEAD_SOURCE_IDEMPOTENCY_KEY_MAX).nullable(),
    payloadHash: z.string().min(1).max(LEAD_SOURCE_PAYLOAD_HASH_MAX).nullable(),
    payloadPurgedAt: z.string().datetime().nullable(),
    metadata: z.record(z.unknown()),
    receivedAt: z.string().datetime(),
  })
  .strict();
export type LeadSourceDeliveryLog = z.infer<typeof LeadSourceDeliveryLogSchema>;

/**
 * @aiPurpose debug-only projection that adds the raw payload back in.
 *   Backend must gate behind `sources.manage` and refuse when
 *   `payloadPurgedAt IS NOT NULL`.
 * @aiSecurity never surface this through ordinary list/read endpoints.
 */
export const LeadSourceDeliveryLogWithRawPayloadSchema = LeadSourceDeliveryLogSchema.extend({
  rawPayload: z.unknown().nullable(),
}).strict();
export type LeadSourceDeliveryLogWithRawPayload = z.infer<
  typeof LeadSourceDeliveryLogWithRawPayloadSchema
>;

/**
 * @aiPurpose admin input to create a new `LeadSource`. The HMAC secret
 *   is minted server-side; never accepted from the caller. Sources are
 *   created enabled by default.
 */
export const CreateLeadSourceInputSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .max(LEAD_SOURCE_CODE_MAX)
      .regex(LEAD_SOURCE_CODE_REGEX, 'must be lowercase letters/digits/underscore'),
    displayName: z.string().min(1).max(LEAD_SOURCE_DISPLAY_NAME_MAX),
  })
  .strict();
export type CreateLeadSourceInput = z.infer<typeof CreateLeadSourceInputSchema>;

/**
 * @aiPurpose admin input to mutate an existing `LeadSource`. `code` is
 *   immutable post-create so partner integrations and intake URLs do
 *   not drift; rename the displayName or toggle enabled/archived.
 */
export const UpdateLeadSourceInputSchema = z
  .object({
    displayName: z.string().min(1).max(LEAD_SOURCE_DISPLAY_NAME_MAX).optional(),
    enabled: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'at least one editable field must be provided',
  });
export type UpdateLeadSourceInput = z.infer<typeof UpdateLeadSourceInputSchema>;

/**
 * @aiPurpose webhook intake request body (#1078). Source partners POST
 *   this JSON shape — the HTTP layer wraps it with HMAC headers (see
 *   `LEAD_SOURCE_INTAKE_HEADERS`).
 * @aiPii fields are the same PII surface as `LeadSchema` (`name`,
 *   `phone`, `email`, `comment`). The Lead row created from intake
 *   inherits the field-level redaction contract.
 */
export const LeadSourceIntakeBodySchema = z
  .object({
    name: z.string().min(1).max(200),
    phone: z.string().min(1).max(32).nullable().optional(),
    email: z.string().email().max(320).nullable().optional(),
    comment: z.string().max(2000).nullable().optional(),
    /// Free-form partner-side metadata; intake stores it on the
    /// delivery log `metadata.partner` slot but never on the Lead row.
    partnerMetadata: z.record(z.unknown()).optional(),
  })
  .strict();
export type LeadSourceIntakeBody = z.infer<typeof LeadSourceIntakeBodySchema>;

/**
 * @aiPurpose header names the intake controller reads to authenticate
 *   a webhook delivery. Centralized here so partner integration guides
 *   and the controller never drift.
 * @aiSecurity `LEAD_SOURCE_TIMESTAMP_SKEW_SECONDS` defines the
 *   acceptable +/- window for the `x-magimp-timestamp` header. Outside
 *   the window → `REJECTED_TIMESTAMP`.
 */
export const LEAD_SOURCE_INTAKE_HEADERS = {
  timestamp: 'x-magimp-timestamp',
  nonce: 'x-magimp-nonce',
  signature: 'x-magimp-signature',
  idempotencyKey: 'idempotency-key',
} as const;
export const LEAD_SOURCE_TIMESTAMP_SKEW_SECONDS = 5 * 60;
export const LEAD_SOURCE_NONCE_RETENTION_SECONDS = LEAD_SOURCE_TIMESTAMP_SKEW_SECONDS + 60;
export const LEAD_SOURCE_PAYLOAD_RETENTION_DAYS = 7;
export const LEAD_SOURCE_LOG_RETENTION_DAYS = 90;

/**
 * @aiPurpose intake response envelope (#1078). On success returns the
 *   new Lead's `publicCode` plus the delivery log id so a partner can
 *   trace its own delivery; rejection bodies stay minimal and never
 *   leak server-side state (no stack traces, no row ids, no PII).
 */
export const LeadSourceIntakeAcceptedSchema = z
  .object({
    outcome: z.literal(LeadSourceDeliveryOutcome.ACCEPTED),
    leadPublicCode: z.string(),
    deliveryLogId: z.string().uuid(),
  })
  .strict();
export type LeadSourceIntakeAccepted = z.infer<typeof LeadSourceIntakeAcceptedSchema>;

export const LeadSourceIntakeRejectedSchema = z
  .object({
    outcome: LeadSourceDeliveryOutcomeSchema,
    message: z.string(),
  })
  .strict();
export type LeadSourceIntakeRejected = z.infer<typeof LeadSourceIntakeRejectedSchema>;

/**
 * @aiPurpose lean `{id, displayName}` projection used by the
 *   `/leads` source-filter dropdown (#1285). Intentionally narrower
 *   than `LeadSourceSchema` — only the display pair is needed, and
 *   the endpoint is gated by `leads.read`, not `sources.read`.
 * @aiSecurity MUST NOT include `secret`, `code`, `enabled`, or any
 *   audit timestamp. The gate rationale: `{id,displayName}` is a
 *   lean read decoupled from the admin source-catalog permission; any
 *   future read-only leads actor automatically gets filter access.
 */
export const LeadSourceOptionSchema = z
  .object({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(LEAD_SOURCE_DISPLAY_NAME_MAX),
  })
  .strict();
export type LeadSourceOption = z.infer<typeof LeadSourceOptionSchema>;

export const LeadSourceOptionsResponseSchema = z.array(LeadSourceOptionSchema);
export type LeadSourceOptionsResponse = z.infer<typeof LeadSourceOptionsResponseSchema>;

/**
 * @aiPurpose pagination cursor for `GET /lead-sources`. Mirrors the
 *   simple offset-style cursor pattern used by lower-volume admin
 *   endpoints elsewhere in the repo.
 */
export const LeadSourceListQuerySchema = z
  .object({
    includeArchived: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict();
export type LeadSourceListQuery = z.infer<typeof LeadSourceListQuerySchema>;

/**
 * @aiPurpose query shape for `GET /lead-sources/:code/delivery-logs`.
 */
export const LeadSourceDeliveryLogQuerySchema = z
  .object({
    outcome: LeadSourceDeliveryOutcomeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();
export type LeadSourceDeliveryLogQuery = z.infer<typeof LeadSourceDeliveryLogQuerySchema>;
